import { ProductType, type ProductImage } from './catalog';

/**
 * Movement kinds recorded in the stock ledger.
 *
 * Every balance change writes one of these, so the ledger fully explains how a branch arrived at
 * its current quantity. A transfer writes two: `TRANSFER_OUT` at the source when it is dispatched
 * and `TRANSFER_IN` at the destination when it is received.
 */
export const StockTransactionType = {
  RECEIPT: 'RECEIPT',
  ISSUE: 'ISSUE',
  ADJUSTMENT: 'ADJUSTMENT',
  COUNT: 'COUNT',
  TRANSFER_IN: 'TRANSFER_IN',
  TRANSFER_OUT: 'TRANSFER_OUT',
  /** Undoes one ISSUE exactly, pointing at it. The issue itself is never changed. */
  ISSUE_REVERSAL: 'ISSUE_REVERSAL',
} as const;
export type StockTransactionType = (typeof StockTransactionType)[keyof typeof StockTransactionType];

/**
 * Product types whose variants are batch-tracked unless told otherwise.
 *
 * Contact lenses and care solutions carry a printed expiry that staff must be able to check
 * before dispensing. Frames do not, and forcing a lot number onto every frame would be busywork
 * that ends in fabricated batch numbers — hence the hybrid model rather than a global switch.
 */
export const BATCH_TRACKED_BY_DEFAULT: readonly ProductType[] = [
  ProductType.CONTACT_LENS,
  ProductType.SOLUTION,
];

export function defaultBatchTracking(type: ProductType): boolean {
  return BATCH_TRACKED_BY_DEFAULT.includes(type);
}

export interface ProductCategorySummary {
  id: string;
  name: string;
  nameAr: string | null;
  parentId: string | null;
  parentName: string | null;
  parentNameAr: string | null;
  isActive: boolean;
  productCount: number;
}

export interface ProductBatchSummary {
  id: string;
  variantId: string;
  batchNumber: string;
  expiryDate: string | null;
  manufacturedDate: string | null;
  supplierReference: string | null;
  isActive: boolean;
  /** Quantity in the branch the request is scoped to, when one is active. */
  quantityInBranch: number | null;
  /** Null when the batch has no expiry date. Negative once expired. */
  daysUntilExpiry: number | null;
}

export interface StockLevel {
  id: string;
  branchId: string;
  branchName: string;
  variantId: string;
  sku: string;
  variantName: string | null;
  productId: string;
  productName: string;
  productType: ProductType;
  brandName: string | null;
  quantityOnHand: number;
  reorderPoint: number | null;
  reorderQuantity: number | null;
  isBatchTracked: boolean;
  /** The variant's photograph, falling back to the product's. */
  image: ProductImage | null;
  isLowStock: boolean;
  isOutOfStock: boolean;
  lastCountedAt: string | null;
  updatedAt: string;
}

export interface StockTransactionSummary {
  id: string;
  type: StockTransactionType;
  branchId: string;
  branchName: string;
  variantId: string;
  sku: string;
  productName: string;
  batchId: string | null;
  batchNumber: string | null;
  quantityDelta: number;
  balanceAfter: number;
  reason: string | null;
  referenceType: string | null;
  referenceId: string | null;
  actorName: string | null;
  createdAt: string;
}

/** Everything the alerts screen needs, in one request. */
export interface StockAlerts {
  lowStock: StockLevel[];
  outOfStock: StockLevel[];
  expiringSoon: (ProductBatchSummary & { sku: string; productName: string; branchName: string })[];
  expired: (ProductBatchSummary & { sku: string; productName: string; branchName: string })[];
  counts: {
    lowStock: number;
    outOfStock: number;
    expiringSoon: number;
    expired: number;
  };
}

/** Days ahead that counts as "expiring soon" unless a caller asks for another window. */
export const DEFAULT_EXPIRY_WARNING_DAYS = 90;

// ---------------------------------------------------------------------------
// Branch transfers
// ---------------------------------------------------------------------------

/**
 * Lifecycle of a branch transfer.
 *
 * A transfer may only be cancelled while it is still a `DRAFT`. Once stock has left the source
 * branch it has to be received — even at a quantity of zero — so that a loss in transit is
 * recorded rather than erased.
 *
 * A shipment can arrive in instalments, so receiving is not a single event:
 *   - `PARTIALLY_RECEIVED` — some of it has landed and more is still expected.
 *   - `RECEIVED` — every line has been counted in at exactly the dispatched quantity.
 *   - `RECEIVED_WITH_VARIANCE` — deliberately closed while the totals still disagree.
 *
 * The last one is a decision, not an arithmetic outcome: it says someone stopped waiting and
 * accepted the shortage. The difference is kept on the line, and no compensating movement is
 * invented to make the books balance.
 */
export const StockTransferStatus = {
  DRAFT: 'DRAFT',
  DISPATCHED: 'DISPATCHED',
  PARTIALLY_RECEIVED: 'PARTIALLY_RECEIVED',
  RECEIVED: 'RECEIVED',
  RECEIVED_WITH_VARIANCE: 'RECEIVED_WITH_VARIANCE',
  CANCELLED: 'CANCELLED',
} as const;
export type StockTransferStatus = (typeof StockTransferStatus)[keyof typeof StockTransferStatus];

/** Statuses in which a transfer is finished and will accept no further receipts. */
export const CLOSED_TRANSFER_STATUSES: readonly StockTransferStatus[] = [
  'RECEIVED',
  'RECEIVED_WITH_VARIANCE',
  'CANCELLED',
];

export function isTransferClosed(status: StockTransferStatus): boolean {
  return CLOSED_TRANSFER_STATUSES.includes(status);
}

export interface StockTransferLineSummary {
  id: string;
  variantId: string;
  sku: string;
  productName: string;
  variantName: string | null;
  isBatchTracked: boolean;
  batchId: string | null;
  batchNumber: string | null;
  batchExpiryDate: string | null;
  quantity: number;
  /** Cumulative across every receipt. Null until the first one. */
  receivedQuantity: number | null;
  /** `receivedQuantity - quantity`, so negative is a shortfall. Null until first received. */
  variance: number | null;
  /** Still expected: `quantity - receivedQuantity`, floored at zero. */
  outstandingQuantity: number;
  /** What the source branch holds right now, when the caller can see it. */
  availableAtSource: number | null;
  notes: string | null;
}

export interface StockTransferSummary {
  id: string;
  reference: string;
  status: StockTransferStatus;
  /** Moves by one on every change. Editing, dispatching, receiving and cancelling send back the version the screen showed; an older one is refused as stale. */
  version: number;
  sourceBranchId: string;
  sourceBranchName: string;
  destinationBranchId: string;
  destinationBranchName: string;
  lineCount: number;
  totalQuantity: number;
  totalReceivedQuantity: number | null;
  /** Dispatched but not yet counted in anywhere. Zero once every line is fully received. */
  totalOutstandingQuantity: number;
  /** True when at least one line has been received short or over. */
  hasVariance: boolean;
  /** False once the transfer is received, closed with a variance, or cancelled. */
  isOpen: boolean;
  notes: string | null;
  dispatchedAt: string | null;
  receivedAt: string | null;
  cancelledAt: string | null;
  cancelledReason: string | null;
  createdByName: string | null;
  dispatchedByName: string | null;
  receivedByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface StockTransferDetail extends StockTransferSummary {
  lines: StockTransferLineSummary[];
}

/** What a branch has dispatched but not yet had received, per variant. */
export interface InTransitLine {
  variantId: string;
  sku: string;
  productName: string;
  quantity: number;
  transferCount: number;
}

// ---------------------------------------------------------------------------
// Stocktake
// ---------------------------------------------------------------------------

/**
 * Lifecycle of a physical count.
 *
 * `OPEN` is a worksheet: expected quantities are snapshotted and staff enter what they find, but
 * no stock moves. `POSTED` is the single moment every variance becomes a movement. There is no
 * approval step.
 */
export const StocktakeStatus = {
  OPEN: 'OPEN',
  POSTED: 'POSTED',
  CANCELLED: 'CANCELLED',
} as const;
export type StocktakeStatus = (typeof StocktakeStatus)[keyof typeof StocktakeStatus];

export interface StocktakeLineSummary {
  id: string;
  variantId: string;
  sku: string;
  productName: string;
  variantName: string | null;
  isBatchTracked: boolean;
  batchId: string | null;
  batchNumber: string | null;
  batchExpiryDate: string | null;
  /** What the system held when the session was opened. */
  expectedQuantityAtOpen: number;
  /**
   * What the system held when this line was counted, derived from the ledger movements between the
   * session opening and `lastCountedAt`. Null until the line is counted.
   */
  expectedQuantityAtCount: number | null;
  /** What was physically counted. Null until someone enters a figure. */
  countedQuantity: number | null;
  /**
   * The physical discrepancy: `countedQuantity - expectedQuantityAtCount`.
   *
   * This is the figure a stocktake exists to find, and the only one posting applies. Movements
   * made after the count are legitimate and are deliberately excluded from it.
   */
  varianceAtCount: number | null;
  /**
   * `countedQuantity - expectedQuantityAtOpen` — how far the number moved since counting began,
   * for context only. It includes legitimate sales and is not what gets corrected.
   */
  varianceAtOpen: number | null;
  /** Net legitimate movement between the session opening and this line being counted. */
  movementBeforeCount: number | null;
  /** The system balance at the moment of posting. Null until posted. */
  postedSystemQuantity: number | null;
  notes: string | null;
  lastCountedAt: string | null;
  countedByName: string | null;
}

export interface StocktakeSummary {
  id: string;
  reference: string;
  status: StocktakeStatus;
  branchId: string;
  branchName: string;
  name: string | null;
  notes: string | null;
  scopeCategoryId: string | null;
  scopeCategoryName: string | null;
  scopeProductType: ProductType | null;
  lineCount: number;
  countedLineCount: number;
  /** Counted lines with a real discrepancy at count time. */
  varianceLineCount: number;
  /** Net units posting would add or remove, summed from each line's count-time variance. */
  netVariance: number;
  /** Units counted short plus units counted over, ignoring sign — the size of the discrepancy. */
  absoluteVariance: number;
  isComplete: boolean;
  postedAt: string | null;
  cancelledAt: string | null;
  cancelledReason: string | null;
  createdByName: string | null;
  postedByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface StocktakeDetail extends StocktakeSummary {
  lines: StocktakeLineSummary[];
}

// ---------------------------------------------------------------------------
// Adjustments
// ---------------------------------------------------------------------------

/**
 * The kind of correction a reason code represents.
 *
 * A tenant writes its own reason wording; the type is what reporting groups on, so "breakage",
 * "broken in fitting" and "كسر" all roll up to DAMAGE without anyone maintaining a synonym list.
 */
export const StockAdjustmentType = {
  DAMAGE: 'DAMAGE',
  LOSS: 'LOSS',
  FOUND: 'FOUND',
  DATA_CORRECTION: 'DATA_CORRECTION',
  OPENING_CORRECTION: 'OPENING_CORRECTION',
  OTHER: 'OTHER',
} as const;
export type StockAdjustmentType = (typeof StockAdjustmentType)[keyof typeof StockAdjustmentType];

export interface StockAdjustmentReasonSummary {
  id: string;
  code: string;
  name: string;
  nameAr: string | null;
  type: StockAdjustmentType;
  isSystem: boolean;
  isActive: boolean;
  requiresNote: boolean;
  sortOrder: number;
  /** How many ledger rows cite it. A used reason can be deactivated but never deleted. */
  usageCount?: number;
}

/**
 * Reason codes every tenant starts with.
 *
 * Seeded rather than hardcoded: a tenant can rename, deactivate or extend them, but starting with
 * an empty list would mean stock could not be corrected at all on day one.
 */
export const DEFAULT_ADJUSTMENT_REASONS: readonly {
  code: string;
  name: string;
  nameAr: string;
  type: StockAdjustmentType;
  requiresNote: boolean;
  sortOrder: number;
}[] = [
  {
    code: 'DAMAGE',
    name: 'Damaged',
    nameAr: 'تالف',
    type: StockAdjustmentType.DAMAGE,
    requiresNote: true,
    sortOrder: 10,
  },
  {
    code: 'LOSS',
    name: 'Lost or stolen',
    nameAr: 'مفقود أو مسروق',
    type: StockAdjustmentType.LOSS,
    requiresNote: true,
    sortOrder: 20,
  },
  {
    code: 'FOUND',
    name: 'Found',
    nameAr: 'تم العثور عليه',
    type: StockAdjustmentType.FOUND,
    requiresNote: false,
    sortOrder: 30,
  },
  {
    code: 'EXPIRED_DISPOSAL',
    name: 'Expired — disposed',
    nameAr: 'منتهي الصلاحية — تم التخلص منه',
    type: StockAdjustmentType.LOSS,
    requiresNote: false,
    sortOrder: 40,
  },
  {
    code: 'DATA_CORRECTION',
    name: 'Data correction',
    nameAr: 'تصحيح بيانات',
    type: StockAdjustmentType.DATA_CORRECTION,
    requiresNote: true,
    sortOrder: 50,
  },
  {
    code: 'OPENING_BALANCE',
    name: 'Opening balance',
    nameAr: 'رصيد افتتاحي',
    type: StockAdjustmentType.OPENING_CORRECTION,
    requiresNote: false,
    sortOrder: 60,
  },
  {
    code: 'OTHER',
    name: 'Other',
    nameAr: 'أخرى',
    type: StockAdjustmentType.OTHER,
    requiresNote: true,
    sortOrder: 99,
  },
];

// ---------------------------------------------------------------------------
// Inventory settings
// ---------------------------------------------------------------------------

/** Tenant thresholds for the judgements inventory makes automatically. */
export interface InventorySettingsSummary {
  expiringSoonDays: number;
  criticalExpiryDays: number;
  slowMovingDays: number;
  deadStockDays: number;
  minimumOnHandForSlowMoving: number;
}

export const DEFAULT_INVENTORY_SETTINGS: InventorySettingsSummary = {
  expiringSoonDays: 90,
  criticalExpiryDays: 30,
  slowMovingDays: 90,
  deadStockDays: 180,
  minimumOnHandForSlowMoving: 1,
};

/** How close a lot is to being unusable. Derived from the expiry date and the tenant's thresholds. */
export const BatchExpiryState = {
  NORMAL: 'NORMAL',
  EXPIRING_SOON: 'EXPIRING_SOON',
  EXPIRED: 'EXPIRED',
} as const;
export type BatchExpiryState = (typeof BatchExpiryState)[keyof typeof BatchExpiryState];

// ---------------------------------------------------------------------------
// Stock movement classification
// ---------------------------------------------------------------------------

/**
 * How stagnant a line is.
 *
 * `DEAD` is a strictly stronger claim than `SLOW_MOVING` — capital tied up rather than merely
 * quiet — so a line is only ever one of them, never both.
 */
export const StockMovementClass = {
  ACTIVE: 'ACTIVE',
  SLOW_MOVING: 'SLOW_MOVING',
  DEAD: 'DEAD',
} as const;
export type StockMovementClass = (typeof StockMovementClass)[keyof typeof StockMovementClass];

export interface StagnantStockLine {
  variantId: string;
  sku: string;
  productName: string;
  variantName: string | null;
  branchId: string;
  branchName: string;
  quantityOnHand: number;
  classification: StockMovementClass;
  /** Last outbound movement — a sale, an issue, a transfer out. Null if it has never left. */
  lastSaleAt: string | null;
  /** First time the branch received any. The clock starts here when nothing has ever sold. */
  firstReceivedAt: string | null;
  /** Days since the reference date, which is the last sale or, failing that, the first receipt. */
  daysWithoutSale: number;
  /** True when the item has never sold and is judged purely on how long it has sat. */
  neverSold: boolean;
  /** `costPriceMinor × quantityOnHand`. Null when no cost is recorded — never inferred from retail. */
  stockValueMinor: number | null;
}

export interface StagnantStockReport {
  lines: StagnantStockLine[];
  /** Summed `stockValueMinor` across lines that have a cost. The headline "capital tied up" figure. */
  totalValueMinor: number;
  /** Lines with no cost price, so the total understates reality by this many lines. */
  linesWithoutCost: number;
  thresholdDays: number;
}

// ---------------------------------------------------------------------------
// Issuing stock
// ---------------------------------------------------------------------------

/**
 * How a batch-tracked issue picks its lots.
 *
 * `FEFO` — first expire, first out — is the default for anything with a shelf life. Dispensing the
 * longest-dated box first leaves the short-dated one to expire on the shelf, which is how stock
 * turns into a write-off. `MANUAL` names the lots explicitly and exists for corrections and for a
 * future authorised override.
 */
export const BatchAllocationStrategy = {
  FEFO: 'FEFO',
  MANUAL: 'MANUAL',
} as const;
export type BatchAllocationStrategy =
  (typeof BatchAllocationStrategy)[keyof typeof BatchAllocationStrategy];

/** One lot's share of an issue. */
export interface BatchAllocation {
  batchId: string;
  batchNumber: string;
  expiryDate: string | null;
  quantity: number;
  /** The ledger row this lot's movement wrote, so a sale line can later reverse exactly that movement. */
  transactionId: string;
}

/** What an issue actually did, so a caller can record it against a sale line. */
export interface StockIssueResult {
  variantId: string;
  branchId: string;
  quantityIssued: number;
  balanceAfter: number;
  /** Empty for a variant that is not batch-tracked. */
  allocations: BatchAllocation[];
  transactionIds: string[];
}
